import * as Yup from "yup";
import { dateIsWithinInterval, dateToyyyyMMdd } from "../../../../util/date";
import { getPatchOperations } from "../../../../util/form";
import { PersonFirma } from "../../../../interfaces/schema";

export const personFirmarelationerForm = (
  personId: string,
  firmaer: PersonFirma[],
  personFirmaId?: string,
): FormConfig<PersonFirmaDTO> => {
  return {
    title: "firmarelation",
    lockedValues: [
      {
        id: "personId",
        value: personId,
      },
      {
        id: "personFirmaId",
        value: personFirmaId,
      },
    ],
    validationSchema: Yup.object({
      firmaId: Yup.string()
        .test(
          "overlappingRelationCheck",
          "Personen har allerede været ansat i dette firma indenfor den valgte periode",
          function (selected) {
            if (this.parent["periodeFraDato"]) {
              const existingFirms = firmaer
                .filter((firmaObj) => firmaObj.firma.id === selected)
                .filter((firm) => firm.id !== personFirmaId);
              if (existingFirms.length > 0) {
                for (const existingFirm of existingFirms) {
                  const period = existingFirm.periode;
                  const selectedPeriod = {
                    periodeFraDato: this.parent["periodeFraDato"],
                    periodeTilDatoInklusiv:
                      this.parent["periodeTilDatoInklusiv"],
                  };
                  const existingRelationPeriodeFraDato = new Date(
                    period.fraDato,
                  );
                  const existingRelationPeriodeTilDatoInklusiv = new Date(
                    period.tilDatoInklusiv,
                  );

                  const selectedPeriodFraDato = new Date(
                    selectedPeriod.periodeFraDato,
                  );
                  const selectedPeriodTilDatoInklusiv = new Date(
                    selectedPeriod.periodeTilDatoInklusiv ?? "9999-12-31",
                  );

                  // Checks if the selected period overlaps with any existing periods
                  if (
                    dateIsWithinInterval(
                      selectedPeriodFraDato,
                      existingRelationPeriodeFraDato,
                      existingRelationPeriodeTilDatoInklusiv,
                    )
                  ) {
                    return false;
                  }
                  if (
                    dateIsWithinInterval(
                      selectedPeriodTilDatoInklusiv,
                      existingRelationPeriodeFraDato,
                      existingRelationPeriodeTilDatoInklusiv,
                    )
                  ) {
                    return false;
                  }

                  // Checks if the existing period overlaps with the selected period
                  if (
                    dateIsWithinInterval(
                      existingRelationPeriodeFraDato,
                      selectedPeriodFraDato,
                      selectedPeriodTilDatoInklusiv,
                    )
                  ) {
                    return false;
                  }
                  if (
                    dateIsWithinInterval(
                      existingRelationPeriodeTilDatoInklusiv,
                      selectedPeriodFraDato,
                      selectedPeriodTilDatoInklusiv,
                    )
                  ) {
                    return false;
                  }
                }
              }
            }
            return true;
          },
        )
        .required("Skal være sat"),
      personfirmatypeNr: Yup.string().required("Relation skal være sat"),
      periodeFraDato: Yup.date().nullable().required("Skal være sat"),
      periodeTilDatoInklusiv: Yup.date().nullable(),
      emailArbejde: Yup.string().email("Skal være en gyldig email"),
      bemaerkninger: Yup.string(),
      priorieteretPrimaerFra: Yup.date().nullable(),
    }),
    form: [
      {
        component: "firma",
        id: "firmaId",
        label: "Firmanavn",
        initialValue: "",
        isRequired: true,
      },
      {
        component: "kode-select",
        id: "personfirmatypeNr",
        kodeListeType: "PersonfirmatypeKode",
        label: "Relation",
        placeholder: "Vælg relation",
        initialValue: "",
        isRequired: true,
      },
      {
        component: "datepicker",
        id: "periodeFraDato",
        initialValue: "",
        label: "Startdato",
        isRequired: true,
      },
      {
        component: "datepicker",
        id: "periodeTilDatoInklusiv",
        initialValue: "",
        label: "Slutdato",
      },
      {
        component: "textinput",
        id: "emailArbejde",
        initialValue: "",
        label: "E-mail arbejde",
        placeholder: "person@email.com",
      },
      {
        component: "textarea",
        id: "bemaerkninger",
        initialValue: "",
        label: "Bemærkninger",
      },
      {
        component: "textinput",
        id: "fakturaReference",
        initialValue: "",
        label: "Fakturareference advokatuddannelsen",
      },
      {
        component: "datepicker",
        id: "priorieteretPrimaerFra",
        label: "Prioriteringsdato",
        initialValue: "",
      },
    ],
    get: `person/${personId}/firmaer/${personFirmaId}`,
    post: (values: PersonFirmaDTO) => {
      const body = {
        ...values,
        periodeFraDato: dateToyyyyMMdd(values.periodeFraDato),
        periodeTilDatoInklusiv: values.periodeTilDatoInklusiv
          ? dateToyyyyMMdd(values.periodeTilDatoInklusiv)
          : "9999-12-31",
        priorieteretPrimaerFra: values.priorieteretPrimaerFra
          ? dateToyyyyMMdd(values.priorieteretPrimaerFra)
          : null,
      };
      return {
        query: `person/${personId}/firmaer`,
        body,
      };
    },
    patch: (values: PersonFirmaDTO) => {
      const ops = getPatchOperations(values);
      return {
        query: `person/${personId}/firmaer/${personFirmaId}`,
        body: JSON.stringify(ops),
      };
    },
    delete: () => {
      return {
        query: `person/${personId}/firmaer/${personFirmaId}`,
      };
    },
  };
};
